import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { BookingConsultationStateService } from "../state/booking-consultation-state.service";
import { GoogleEventsMapperService } from "../mappers/google-events-mapper.service";
import { GoogleCalendarEventsApiService } from "../api/calendar/google-calendar-events-api.service";
import { LoggedUserService } from "@keus-template/auth";
import { VisitsApiService } from "../api/healthcare/visits-api.service";
import { ToastService } from "../ui/toast.service";
import {
  CreateEventDto,
  CreateVisitDto,
  UpdateEventDto,
} from "../../api/models";
import { StripeApiService } from "../api/payments/stripe-api.service";
import { environment } from "../../environments/environment";
import {
  catchError,
  finalize,
  from,
  map,
  Observable,
  of,
  switchMap,
  tap,
  throwError,
  timer,
  mergeMap,
  take,
} from "rxjs";
import { loadStripe, Stripe, StripeError } from "@stripe/stripe-js";
import { BookingConsultationState } from "../../interfaces/booking-consultation-state.interface";
import { ConsultationType } from "../../enums/consultation-type.enum";

interface StripeRedirectResult {
  success: boolean;
  error?: StripeError;
}

@Injectable({
  providedIn: "root",
})
export class ProcessConsultationAndStripePaymentsService {
  /** Service that manages the booking consultation state */
  private readonly _bookingPrescriptionStateService: BookingConsultationStateService =
    inject(BookingConsultationStateService);

  /** API service for managing Google Calendar events */
  private readonly _googleCalendarApi: GoogleCalendarEventsApiService = inject(
    GoogleCalendarEventsApiService,
  );

  /** Maps internal state data to Google Calendar event DTOs */
  private readonly _googleEventsMapper: GoogleEventsMapperService = inject(
    GoogleEventsMapperService,
  );

  /** API service for handling Stripe-related operations */
  private readonly _stripeApi: StripeApiService = inject(StripeApiService);

  /** Router service for navigation */
  private readonly _router: Router = inject(Router);

  /** Service for managing logged user data */
  private readonly _loggedUserService: LoggedUserService =
    inject(LoggedUserService);

  /** API service for managing visits */
  private readonly _visitsApi: VisitsApiService = inject(VisitsApiService);

  /** Toast service for showing notifications */
  private readonly _toastService: ToastService = inject(ToastService);

  /** Cached instance of the Stripe client */
  private _stripeInstance: Promise<Stripe | null> | null = null;

  /**
   * Persists consultation and initiates Stripe payment process
   * @returns Observable emitting ConsultationPersistResult with success or error information
   */
  public processConsultationAndPay(): Observable<{ success: boolean; visitId?: string; error?: any }> {
    const stateData = this._bookingPrescriptionStateService.state();

    const validation = this._validateStateData(stateData);
    if (!validation.isValid) {
      console.error("Validation error:", validation.error);
      return of({ success: false, error: validation.error });
    }

    const { doctorId, visitId, bookingDate } =
      this._extractRequiredIds(stateData);

    return this._processConsultation$()
      .pipe(
        switchMap(() => {
          // Bypass Stripe per consulti gratuiti (primi consulti)
          // During registration flow, first consultations are always free
          if (
            stateData.type === ConsultationType.FIRST_VISIT &&
            (stateData.price === 0 ||
              stateData.firstBookingConsultation === true)
          ) {
            console.warn("Bypassing Stripe for free first consultation");

            // If it's the first booking consultation during registration flow,
            // navigate to home page for a better onboarding experience
            if (stateData.firstBookingConsultation === true) {
              this._router.navigate(["/home"]);
            } else {
              // Regular free consultation (already paid/included), go to consultations page
              this._router.navigate(["/my-consultations"], {
                queryParams: { success: "true", type: "free" },
              });
            }

            return of({ success: true });
          }

          // Bypass Stripe per riprogrammazione
          if (stateData.type === ConsultationType.REPROGRAMMING) {
            // For free consultations, no payment scheduling update is needed
            if (stateData.isFree || !stateData.price) {
              this._router.navigate(["/my-consultations"], {
                queryParams: { success: "true", type: "rescheduled" },
              });
              return of({ success: true });
            }

            // CRITICAL: For pre-paid consultations (already paid via package),
            // no payment scheduling update is needed.
            // This prevents creating unnecessary EventBridge schedules for consultations
            // that have already been paid through a package payment.
            if (stateData.paymentStatus === 'paid') {
              console.log('Skipping payment scheduling update for pre-paid consultation:', {
                visitId,
                paymentStatus: stateData.paymentStatus
              });
              this._router.navigate(["/my-consultations"], {
                queryParams: { success: "true", type: "rescheduled" },
              });
              return of({ success: true });
            }

            // For consultations with scheduled payment (paymentStatus='scheduled'),
            // update payment scheduling to reschedule the EventBridge payment trigger
            return this._updatePaymentSchedulingForReprogramming$(
              visitId,
              bookingDate,
            ).pipe(
              tap(() => {
                // Reindirizza alla pagina di consultazioni con successo
                this._router.navigate(["/my-consultations"], {
                  queryParams: { success: "true", type: "rescheduled" },
                });
              }),
              map(() => ({ success: true })),
            );
          }

          // Flusso normale con Stripe per consulti a pagamento
          return this._createStripeSessionAndRedirect$(
            doctorId,
            visitId,
            bookingDate,
          ).pipe(map(() => ({ success: true })));
        }),
        catchError((error) => {
          console.error("Errore nel processo completo:", error);
          return of({ success: false, error: error.message });
        }),
      );
  }

  /**
   * Validates state data before processing
   * @param stateData - current consultation state data
   * @returns Object indicating whether the state is valid and any error message
   */
  private _validateStateData(stateData: any): {
    isValid: boolean;
    error?: string;
  } {
    if (!stateData.doctorId) {
      return { isValid: false, error: "Doctor ID missing" };
    }

    const actionType = this._bookingPrescriptionStateService.getActionType();
    if (
      actionType === "update" &&
      (!stateData.doctorEmail || !stateData.calendarId || !stateData.visitId)
    ) {
      return {
        isValid: false,
        error: "Insufficient data for update operation",
      };
    }

    return { isValid: true };
  }

  /**
   * Extracts required IDs from state
   * @param stateData - current consultation state data
   * @returns Object containing doctorId and visitId
   */
  private _extractRequiredIds(stateData: BookingConsultationState): {
    doctorId: string;
    visitId: string;
    bookingDate: string;
  } {
    const doctorId = stateData.doctorId;
    const visitId = stateData.visitId ?? this._getVisitIdFromMapping(stateData);
    const bookingDate = stateData.bookingDate;

    return {
      doctorId: doctorId || "",
      visitId,
      bookingDate: bookingDate || "",
    };
  }

  /**
   * Gets visitId from mapping when not present in state
   * @param stateData - current consultation state data
   * @returns visitId derived from the mapped event DTO
   */
  private _getVisitIdFromMapping(stateData: BookingConsultationState): string {
    const { dto } = this._googleEventsMapper.mapToEventDto(
      stateData,
      this._bookingPrescriptionStateService.getActionType(),
      "visit",
    );
    return (dto.visit as CreateVisitDto).createdAt || "";
  }

  /**
   * Persists consultation (create or update) based on action type
   * @returns Observable that completes when the operation finishes
   */
  private _processConsultation$(): Observable<void> {
    const stateData = this._bookingPrescriptionStateService.state();
    const action = this._bookingPrescriptionStateService.getActionType();
    const { dto } = this._googleEventsMapper.mapToEventDto(
      stateData,
      action,
      "visit",
    );
    
    const operation$ =
      action === "create"
        ? this._createConsultation$(dto as CreateEventDto)
        : this._updateConsultation$(stateData, dto as UpdateEventDto);

    return operation$.pipe(
      tap(() => {
        // Se è una first visit e l'operazione è completata con successo,
        // aggiorna l'utente e pulisce il session storage
        if (
          stateData.type === ConsultationType.FIRST_VISIT &&
          stateData.firstBookingConsultation
        ) {
          // Update user data in localStorage to reflect registration completion
          const currentUser = this._loggedUserService.currentUser();
          if (currentUser) {
            this._loggedUserService.updateUserData({
              ...currentUser,
              registrationCompleted: true,
            });
          }

          // Clean up session storage
          sessionStorage.removeItem("first-booking-consultation");
          sessionStorage.setItem("registration-completed", "true");
        }
      }),
      catchError((error) => {
        const errorMsg = `Error in consultation ${action}`;
        console.error(errorMsg, error);
        return throwError(() => new Error(`${errorMsg}: ${error.message}`));
      }),
    );

    return of()
  }

  /**
   * Creates a new consultation via Google Calendar API
   * @param dto - data transfer object for creation
   * @returns Observable that completes when creation is done
   */
  private _createConsultation$(dto: CreateEventDto): Observable<void> {
    return this._googleCalendarApi.create$(dto);
  }

  /**
   * Updates an existing consultation via Google Calendar API
   * @param stateData - current consultation state
   * @param dto - data transfer object for update
   * @returns Observable that completes when update is done
   */
  private _updateConsultation$(
    stateData: BookingConsultationState,
    dto: UpdateEventDto,
  ): Observable<void> {
    const { doctorEmail, calendarId, visitId } = stateData;

    if (!doctorEmail || !calendarId || !visitId) {
      return throwError(
        () => new Error("Missing parameters for update operation"),
      );
    }

    return this._googleCalendarApi.update$(
      doctorEmail,
      calendarId,
      visitId,
      dto,
    );
  }

  /**
   * Creates a Stripe checkout session and redirects to Stripe
   * @param doctorId - ID of the doctor
   * @param visitId - ID of the visit
   * @returns Observable with success status or Stripe error
   */
  private _createStripeSessionAndRedirect$(
    doctorId: string,
    visitId: string,
    bookingDate: string,
  ): Observable<StripeRedirectResult> {
    const stateData = this._bookingPrescriptionStateService.state();
    const isPsychologist = stateData.isProfessionalAPsycho;
    
    return this._stripeApi
      .createCheckoutSession$(doctorId, visitId, bookingDate, isPsychologist)
      .pipe(
        switchMap((session) => this._redirectToStripeCheckout$(session.id)),
        map(() => ({ success: true })),
        catchError((error) => {
          console.error("Error in Stripe process:", error);
          return of({ success: false, error });
        }),
      );
  }

  /**
   * Handles the redirect to Stripe Checkout page
   * @param sessionId - Stripe checkout session ID
   * @returns Observable that completes or errors with StripeError
   */
  private _redirectToStripeCheckout$(
    sessionId: string,
  ): Observable<{ error?: StripeError }> {
    return from(this._getStripeInstance()).pipe(
      switchMap((stripe) => {
        if (!stripe) {
          return throwError(() => new Error("Unable to load Stripe"));
        }
        return from(stripe.redirectToCheckout({ sessionId }));
      }),
      catchError((error) => {
        console.error("Error in Stripe redirect:", error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Lazily loads and caches the Stripe instance
   * @returns Promise resolving to Stripe instance or null
   */
  private _getStripeInstance(): Promise<Stripe | null> {
    if (!this._stripeInstance) {
      this._stripeInstance = loadStripe(environment.pkStripe);
    }
    return this._stripeInstance;
  }

  /**
   * Updates payment scheduling for visit rescheduling
   * This bypasses Stripe and directly updates the EventBridge schedule
   * @param visitId - Visit identifier
   * @param bookingDate - New booking date
   * @returns Observable that completes when scheduling is updated
   */
  private _updatePaymentSchedulingForReprogramming$(
    visitId: string,
    bookingDate: string,
  ): Observable<void> {
    return this._stripeApi.updatePaymentScheduling$(visitId, bookingDate).pipe(
      catchError((error) => {
        console.error("Error updating payment scheduling:", error);
        return throwError(
          () =>
            new Error(`Failed to update payment scheduling: ${error.message}`),
        );
      }),
    );
  }

  /**
   * Resets the cached Stripe instance (useful for testing)
   */
  public resetStripeInstance(): void {
    this._stripeInstance = null;
  }

  /**
   * Creates only the visit without Stripe redirect
   * @returns Observable with the visit result
   */
  public createVisitOnly(): Observable<{ success: boolean; visitId?: string; needsSync?: boolean }> {
    const stateData = this._bookingPrescriptionStateService.state();
    
    const validation = this._validateStateData(stateData);
    if (!validation.isValid) {
      console.error("Validation error:", validation.error);
      return of({ success: false });
    }
    
    const { visitId } = this._extractRequiredIds(stateData);
    
    return this._processConsultation$().pipe(
      map(() => {
        // Check if visitId looks like a createdAt timestamp (ISO date format)
        const isCreatedAtFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(visitId);
        
        return { 
          success: true, 
          visitId,
          needsSync: isCreatedAtFormat 
        };
      }),
      catchError((error) => {
        console.error("Error creating visit:", error);
        return of({ success: false });
      })
    );
  }

  /**
   * Retrieves the real visit ID after creation
   * @param doctorId - Doctor ID
   * @param bookingDate - Booking date
   * @returns Observable with the real visit ID
   */
  public retrieveRealVisitId(doctorId: string, bookingDate: string): Observable<string> {
    const user = this._loggedUserService.currentUser();
    const patientId = user?.id;
    
    if (!patientId) {
      return throwError(() => new Error("Patient ID not found"));
    }
    
    return this._retrieveRealVisitId$(doctorId, patientId, bookingDate);
  }

  /**
   * Proceeds to Stripe with a known visit ID
   * @param visitId - The real visit ID
   * @param doctorId - Doctor ID
   * @param bookingDate - Booking date
   * @returns Observable that completes after redirect
   */
  public proceedToStripePayment(
    visitId: string, 
    doctorId: string,
    bookingDate: string
  ): Observable<void> {
    const stateData = this._bookingPrescriptionStateService.state();
    const isPsychologist = stateData.isProfessionalAPsycho;
    
    return this._stripeApi
      .createCheckoutSession$(doctorId, visitId, bookingDate, isPsychologist)
      .pipe(
        switchMap((session) => this._redirectToStripeCheckout$(session.id)),
        map(() => void 0),
        catchError((error) => {
          this._toastService.showError("Errore nel processo di pagamento");
          return throwError(() => error);
        })
      );
  }

  /**
   * Retrieves the real visit ID from the backend by filtering visits
   * @param doctorId - Doctor ID
   * @param patientId - Patient ID
   * @param bookingDate - Booking date
   * @returns Observable with the real visit ID
   */
  private _retrieveRealVisitId$(
    doctorId: string,
    patientId: string,
    bookingDate: string
  ): Observable<string> {
    return timer(1000, 3000).pipe(
      take(6),
      mergeMap((attemptIndex) => 
        this._visitsApi.filter$({ patientId, doctorId, bookingDate }).pipe(
          map(visits => {
            if (visits.length === 0) {
              throw new Error("Visit not found yet");
            }
            const visit = visits[0];
            if (!visit.id) {
              throw new Error("Visit found but ID is missing");
            }
            return visit.id;
          }),
          catchError((error) => {
            if (attemptIndex === 5) {
              return throwError(() => new Error("Failed to retrieve visit ID"));
            }
            return throwError(() => error);
          })
        )
      ),
      take(1)
    );
  }
}
